// Shared metadata + helpers for the Public Model Hub.

// Maps the backend `modelType` values to display metadata.
export const TYPE_META = {
  classification: { label: 'Classification', icon: 'fa-tags', badge: 'info' },
  bounding_box: { label: 'Detection', icon: 'fa-vector-square', badge: 'success' },
  segmentation: { label: 'Segmentation', icon: 'fa-draw-polygon', badge: 'primary' },
};

// Type chips shown in the toolbar (order matters).
export const TYPE_FILTERS = [
  { value: 'classification', ...TYPE_META.classification },
  { value: 'bounding_box', ...TYPE_META.bounding_box },
  { value: 'segmentation', ...TYPE_META.segmentation },
];

export const typeMeta = (type) =>
  TYPE_META[type] || { label: type || 'Model', icon: 'fa-cube', badge: 'neutral' };

// Training writes "complete"; schemas/comments say "completed" — accept both.
export const isTrained = (version) => {
  const s = (version?.trainingStatus || '').toLowerCase();
  return s === 'complete' || s === 'completed';
};

// Most recent trained version (versions arrive newest-first from the API).
export const latestTrainedVersion = (model) =>
  Array.isArray(model?.versions) ? model.versions.find(isTrained) || null : null;

// Distinct class count across a model's versions (best-effort, from the newest version).
export const classCount = (model) => {
  const v = model?.versions?.[0];
  return Array.isArray(v?.classes) ? v.classes.length : 0;
};

export const versionLabel = (version) =>
  version?.versionName || version?.version || version?._id?.slice(-6) || 'v1';

// Compact number formatting for download counts (1.2k etc).
export const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}k`;
  return String(num);
};

// Relative "updated" label — "just now", "3h ago", "5d ago", "2mo ago".
export const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (!then || Number.isNaN(then)) return '';
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

// Absolute date for tooltips — "Sep 4, 2026".
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Stable grouping key for a model's owner — used by BOTH the owner dropdown and the
// owner filter, so they can never disagree. `owner_id` is null on models whose owner doc
// is missing (b_model_management.get_public_models returns None then); those models still
// have to land in one selectable bucket instead of dropping out of the owner list.
export const ownerKey = (model) => {
  const id = model?.owner_id;
  if (id) return String(id);
  const name = String(model?.owner_name || '').trim();
  return name ? `name:${name.toLowerCase()}` : 'unknown';
};

// Owner avatar initials — "Mei Lin" → "ML".
export const ownerInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// Deterministic hue (0-359) from a string, for a stable per-owner avatar colour.
export const ownerHue = (key) => {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

// Human-friendly performance-metric labels.
const METRIC_LABELS = {
  map50: 'mAP@50',
  map5095: 'mAP@50-95',
  miou: 'mIoU',
  pixelacc: 'Pixel Acc.',
  accuracy: 'Accuracy',
  precision: 'Precision',
  recall: 'Recall',
  f1: 'F1 Score',
};
export const formatMetricLabel = (key) => {
  const k = String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return METRIC_LABELS[k] || key;
};
